import { useState, useMemo, useEffect } from "react";
import { Link } from "wouter";
import { useQuery as useConvexQuery } from "convex/react";
import Icon from "@/components/mnf/Icon";
import { GlassCard, SectionTitle, Avatar } from "@/components/mnf/UI";
import RecipeCard from "@/components/mnf/RecipeCard";
import AddRecipeSheet from "@/components/mnf/AddRecipeSheet";
import { useStoreQuery, queries } from "@/lib/store";
import type { Recipe, RecipeSeasonTag } from "@/lib/store";
import { api } from "../../../convex/_generated/api";

/**
 * Nourish discovery hub.
 *
 * Today's Harvest hero, Trending in the garden rail, Your saved list,
 * a By season filter, and a floating Add button that opens AddRecipeSheet
 * (Import URL or Write your own). Tapping any card routes to /nourish/:recipeId.
 */

const SEASONS: { value: RecipeSeasonTag; label: string }[] = [
  { value: "quick",      label: "Quick" },
  { value: "family",     label: "Family" },
  { value: "pregnant",   label: "Pregnant" },
  { value: "postpartum", label: "Postpartum" },
  { value: "nursing",    label: "Nursing" },
];

export default function Nourish() {
  const me = useStoreQuery(queries.currentUser);

  // Tier B: discovery rails read from Convex `recipes` table (seeded via
  // seedRecipes:populateOnce). Cast to Recipe — Convex returns a superset
  // shape (extra _id/_creationTime fields) that's compatible at runtime.
  const featuredConvex = useConvexQuery(api.recipesDb.featuredRecipe, {});
  const featured = (featuredConvex ?? null) as Recipe | null;
  const featuredId = featured?.id;
  const trendingConvex = useConvexQuery(api.recipesDb.trendingRecipes, {
    excludeId: featuredId,
    limit: 4,
  });
  const trending: Recipe[] = (trendingConvex ?? []) as unknown as Recipe[];

  const [activeSeason, setActiveSeason] = useState<RecipeSeasonTag>("quick");
  const seasonConvex = useConvexQuery(api.recipesDb.recipesBySeasonTag, {
    tag: activeSeason,
    limit: 6,
  });
  const seasonRecipes: Recipe[] = (seasonConvex ?? []) as unknown as Recipe[];

  // Convex-persisted: captured + saved. Saves can reference seed recipes too,
  // so we merge the persisted-recipe field with in-memory seed lookups.
  // Skip queries until me?.id is resolved — otherwise we'd hammer Convex
  // with a hardcoded fallback userId and briefly leak demo-user data.
  const capturedConvex = useConvexQuery(
    api.recipesDb.capturedByUser,
    me?.id ? { userId: me.id } : "skip",
  );
  const captured: Recipe[] = (capturedConvex ?? []) as unknown as Recipe[];

  const savedSummaries = useConvexQuery(
    api.recipesDb.savedSummariesByUser,
    me?.id ? { userId: me.id } : "skip",
  );
  const saved: Recipe[] = useMemo(() => {
    if (!savedSummaries) return [];
    const out: Recipe[] = [];
    for (const s of savedSummaries) {
      if (s.persistedRecipe) {
        out.push(s.persistedRecipe as unknown as Recipe);
      } else {
        // Saved recipe id refers to a seed recipe — look up in-memory.
        const seed = queries.recipeById(s.recipeId);
        if (seed) out.push(seed);
      }
    }
    return out;
  }, [savedSummaries]);

  const [addOpen, setAddOpen] = useState(false);
  const [addInitialTab, setAddInitialTab] = useState<"capture" | "import" | "write">("capture");

  // PWA share-target intake. When the app is launched via a share intent we
  // arrive at /?capture=...#/nourish (manifest share_target) or sometimes
  // /#/nourish?capture=URL. Either way, auto-open the sheet on the capture tab.
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const hashQueryStart = hash.indexOf("?");
    const hashParams = hashQueryStart >= 0 ? new URLSearchParams(hash.slice(hashQueryStart + 1)) : new URLSearchParams();
    const searchParams = new URLSearchParams(search);
    if (hashParams.has("capture") || searchParams.has("capture")) {
      setAddInitialTab("capture");
      setAddOpen(true);
      // Clean the URL so a refresh doesn't re-trigger.
      const cleanHash = hashQueryStart >= 0 ? hash.slice(0, hashQueryStart) : hash;
      window.history.replaceState(null, "", window.location.pathname + cleanHash);
    }
  }, []);

  const kileyBundle = useStoreQuery(() =>
    me?.id ? queries.kileySeasonPickForUser(me.id) : null,
  );

  // Recipe search — debounced query against api.recipesDb.searchRecipes.
  // Empty query = no rail; non-empty = replaces trending+season rails so
  // the user's eye stays on the result list.
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 200);
    return () => clearTimeout(t);
  }, [searchInput]);
  const searchHits = useConvexQuery(
    api.recipesDb.searchRecipes,
    searchTerm ? { query: searchTerm, limit: 12 } : "skip",
  );
  const searchActive = searchTerm.length > 0;
  // Dedupe by recipe id — the backend query already scopes to one row per
  // recipe, but this is our last line of defense against a recipe surfacing
  // twice in one result set (e.g. a future search over multiple sources).
  const searchResults: Recipe[] = useMemo(() => {
    const hits = (searchHits ?? []) as unknown as Recipe[];
    const seen = new Set<string>();
    const out: Recipe[] = [];
    for (const r of hits) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      out.push(r);
    }
    return out;
  }, [searchHits]);

  return (
    <>
      <div className="px-5 pt-3 pb-4 space-y-5" style={{ paddingBottom: 120 }}>
        {/* Header */}
        <div>
          <p className="mnf-eyebrow">Nourish · discovery hub</p>
          <h1
            className="mnf-serif leading-[1.05] mt-1"
            style={{ fontSize: 38, fontWeight: 500, color: "var(--mnf-fd)" }}
          >
            Today's<br />Harvest
          </h1>
        </div>

        {/* Kiley's Pick for Your Season strip — hidden while searching so it
            can't slip an unmatched recipe into the results, and can't
            duplicate a recipe that also shows up in the search list. */}
        {!searchActive && kileyBundle && (
          <Link
            href="/kileys-pick"
            className="no-underline block"
            data-testid="link-nourish-kileys-pick"
          >
            <GlassCard
              organic
              padding={14}
              style={{
                background:
                  "linear-gradient(135deg, rgba(192,120,74,0.16) 0%, rgba(245,240,232,0.85) 100%)",
                border: "1px solid rgba(160,120,32,0.28)",
                cursor: "pointer",
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  aria-hidden
                  className="shrink-0 inline-flex items-center justify-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "18px 6px 18px 6px",
                    background: kileyBundle.recipe.heroGradient,
                    color: "#F5F0E8",
                    fontSize: 24,
                  }}
                >
                  {kileyBundle.recipe.heroEmojis[0] ?? "🌿"}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="mnf-eyebrow"
                    style={{ color: "var(--mnf-gold, #A07820)", marginBottom: 2 }}
                  >
                    Coach Kiley picked this for you
                  </p>
                  <p
                    className="mnf-serif"
                    style={{
                      fontSize: 16,
                      fontWeight: 500,
                      color: "var(--mnf-fd)",
                      lineHeight: 1.3,
                    }}
                  >
                    {kileyBundle.recipe.title}
                  </p>
                  <p
                    className="mnf-body italic"
                    style={{
                      fontSize: 12,
                      color: "var(--mnf-tg)",
                      lineHeight: 1.4,
                      marginTop: 2,
                    }}
                  >
                    {kileyBundle.pick.focus}
                  </p>
                </div>
                <Avatar initials="KM" size={36} ringColor="rgba(160,120,32,0.55)" />
              </div>
            </GlassCard>
          </Link>
        )}

        {/* Today's Harvest hero — hide when searching to keep focus on results */}
        {!searchActive && featured && (
          <RecipeCard recipe={featured} variant="hero" eyebrow="Today's Harvest" />
        )}

        {/* Search box */}
        <div className="relative">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by title or ingredient…"
            aria-label="Search recipes"
            className="mnf-body w-full"
            style={{
              padding: "10px 14px 10px 40px",
              borderRadius: 14,
              background: "rgba(255,255,255,0.7)",
              border: "1px solid rgba(30,74,53,0.16)",
              fontSize: 14,
              color: "var(--mnf-tx)",
            }}
            data-testid="input-recipe-search"
          />
          <Icon
            name="search"
            size={16}
            color="var(--mnf-tg)"
            style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }}
          />
          {searchActive && (
            <button
              onClick={() => { setSearchInput(""); setSearchTerm(""); }}
              aria-label="Clear search"
              style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}
            >
              <Icon name="close" size={16} color="var(--mnf-tg)" />
            </button>
          )}
        </div>

        {/* Search results — replace trending+season when searchActive */}
        {searchActive && (
          <>
            <SectionTitle
              eyebrow="Search"
              title={`Results for "${searchTerm}"`}
              trailing={searchHits === undefined ? "…" : `${searchResults.length}`}
            />
            {searchHits === undefined ? (
              <p className="mnf-body" style={{ color: "var(--mnf-tg)", fontSize: 13 }}>
                Searching…
              </p>
            ) : searchResults.length === 0 ? (
              <div
                className="mnf-fade-up"
                data-testid="empty-search-results"
                style={{
                  borderRadius: 18,
                  padding: "20px 18px",
                  background: "rgba(30,74,53,0.04)",
                  border: "1px dashed rgba(30,74,53,0.18)",
                  textAlign: "center",
                }}
              >
                <p
                  className="mnf-serif"
                  style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)", marginBottom: 4 }}
                >
                  Nothing turned up
                </p>
                <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
                  We couldn't find a recipe for "{searchTerm}". Try a different word, or check the spelling.
                </p>
              </div>
            ) : (
              <div className="space-y-2.5 mnf-fade-up">
                {searchResults.map((r) => (
                  <RecipeCard key={r.id} recipe={r} variant="compact" />
                ))}
              </div>
            )}
          </>
        )}

        {/* Trending */}
        {!searchActive && (<>
        <SectionTitle
          eyebrow="In season"
          title="Trending in the garden"
          trailing={`${trending.length} sisters cooking`}
        />
        <div className="space-y-2.5 mnf-fade-up">
          {trending.map((r) => (
            <RecipeCard key={r.id} recipe={r} variant="compact" />
          ))}
        </div>
        </>)}

        {/* Captured by you — hidden while searching; it renders unfiltered
            and would otherwise duplicate a recipe already in the results. */}
        {!searchActive && (<>
        <SectionTitle
          eyebrow="Your cookbook"
          title="Captured by you"
          trailing={captured.length > 0 ? `${captured.length} saved` : undefined}
        />
        {captured.length === 0 ? (
          <button
            type="button"
            data-testid="button-empty-captured"
            onClick={() => { setAddInitialTab("capture"); setAddOpen(true); }}
            className="mnf-fade-up hover-elevate w-full text-left"
            style={{
              borderRadius: 18,
              padding: "18px 16px",
              background: "rgba(127,119,221,0.06)",
              border: "1px dashed rgba(127,119,221,0.32)",
              cursor: "pointer",
            }}
          >
            <div className="flex items-center gap-3">
              <div
                aria-hidden
                className="shrink-0 inline-flex items-center justify-center"
                style={{
                  width: 38, height: 38, borderRadius: 12,
                  background: "rgba(127,119,221,0.18)", color: "#5a52b8",
                }}
              >
                <Icon name="auto_awesome" size={20} />
              </div>
              <div className="flex-1">
                <p className="mnf-serif" style={{ fontSize: 14.5, fontWeight: 500, color: "var(--mnf-fd)" }}>
                  Capture a recipe from anywhere
                </p>
                <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.45, marginTop: 2 }}>
                  Paste a caption from Instagram, TikTok, or a blog and we'll build the cookbook entry.
                </p>
              </div>
            </div>
          </button>
        ) : (
          <div className="space-y-2.5 mnf-fade-up">
            {captured.map((r) => (
              <RecipeCard key={r.id} recipe={r} variant="compact" badge="Captured by you" />
            ))}
          </div>
        )}
        </>)}

        {/* Your saved — hidden while searching; same duplication risk as
            Captured by you above. */}
        {!searchActive && (<>
        <SectionTitle
          eyebrow="Your shelf"
          title="Your saved"
          trailing={saved.length > 0 ? `${saved.length} saved` : undefined}
        />
        {saved.length === 0 ? (
          <div
            className="mnf-fade-up"
            style={{
              borderRadius: 18,
              padding: "20px 18px",
              background: "rgba(30,74,53,0.04)",
              border: "1px dashed rgba(30,74,53,0.18)",
              textAlign: "center",
            }}
          >
            <p
              className="mnf-serif"
              style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)", marginBottom: 4 }}
            >
              Nothing saved yet
            </p>
            <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
              Tap the bookmark on any recipe and it lands here, ready when you are.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5 mnf-fade-up">
            {saved.map((r) => (
              <RecipeCard key={r.id} recipe={r} variant="compact" />
            ))}
          </div>
        )}
        </>)}

        {/* By season — hidden while searching; same duplication risk as the
            sections above. */}
        {!searchActive && (<>
        <SectionTitle eyebrow="Find your season" title="By season" />
        <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
          {SEASONS.map((s) => {
            const active = activeSeason === s.value;
            return (
              <button
                key={s.value}
                data-testid={`pill-season-${s.value}`}
                onClick={() => setActiveSeason(s.value)}
                className="mnf-body shrink-0 transition-all hover-elevate"
                style={{
                  padding: "8px 14px",
                  borderRadius: 999,
                  background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                  color: active ? "#F5F0E8" : "var(--mnf-fd)",
                  fontSize: 12.5,
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  border: "1px solid transparent",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        {seasonRecipes.length === 0 ? (
          <div
            className="mnf-fade-up"
            style={{
              borderRadius: 18,
              padding: "18px 16px",
              background: "rgba(30,74,53,0.04)",
              border: "1px dashed rgba(30,74,53,0.18)",
            }}
          >
            <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", textAlign: "center" }}>
              No recipes for this season yet. Add one below to plant the first seed.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5 mnf-fade-up">
            {seasonRecipes.map((r) => (
              <RecipeCard key={r.id} recipe={r} variant="compact" />
            ))}
          </div>
        )}
        </>)}

        <div className="h-2" />
      </div>

      {/* Floating add button — disabled until me is loaded so we never
          send a request with the fallback demo userId. */}
      <button
        type="button"
        data-testid="button-add-recipe"
        onClick={() => setAddOpen(true)}
        disabled={!me?.id}
        aria-label="Add recipe"
        className="hover-elevate"
        style={{
          position: "fixed",
          bottom: 84,
          right: 20,
          zIndex: 40,
          width: 56,
          height: 56,
          borderRadius: 999,
          background: "var(--mnf-t)",
          color: "#F5F0E8",
          border: "1px solid rgba(255,255,255,0.18)",
          boxShadow: "0 12px 28px rgba(192,120,74,0.42)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: me?.id ? "pointer" : "not-allowed",
          opacity: me?.id ? 1 : 0.5,
        }}
      >
        <Icon name="add" size={26} />
      </button>

      {me?.id && (
        <AddRecipeSheet
          open={addOpen}
          onClose={() => setAddOpen(false)}
          authorId={me.id}
          initialTab={addInitialTab}
        />
      )}
    </>
  );
}
